import styled from '@emotion/styled';
import React, { RefObject, useCallback, useEffect, useRef } from 'react';
import useStreamingVideo from '../../utils/hooks/useStreamingVideo';
import { useOutsideClick } from '../../utils/hooks/useOutsideClick';
import OverlayMedia from './media/OverlayMedia';
import { sendViewVideoCompletedEvent, sendViewVideoEvent } from '../../utils/analytics/video';

const FullVideo = styled.video`
  object-fit: contain;
  height: 100%;
  width: 100%;
`;

type Props = {
  src?: string;
  onClose: ({ videoRef }: { videoRef: RefObject<HTMLVideoElement> }) => void;
  aspectRatio?: number;
  startTime?: number;
  videoRef: RefObject<HTMLVideoElement>;
  videoId: number;
};

export default function OverlayVideoPlayer({
  src,
  onClose,
  aspectRatio = 9 / 16,
  startTime = undefined,
  videoRef,
  videoId,
}: Props) {
  const { doneLoading: doneLoadingScript, play } = useStreamingVideo({ src, ref: videoRef });
  const boxRef = useRef(null);
  const closeVideoHandler = useCallback(() => {
    sendViewVideoCompletedEvent({ videoId, videoRef });
    onClose({ videoRef });
  }, [onClose, videoId, videoRef]);

  useOutsideClick(boxRef, () => closeVideoHandler());

  useEffect(() => {
    if (doneLoadingScript) {
      sendViewVideoEvent({ videoId });
      play();
    }
  }, [doneLoadingScript, play, videoId]);

  useEffect(() => {
    const currentRef = videoRef.current;
    const onEvent = () => closeVideoHandler();
    currentRef?.addEventListener('webkitendfullscreen', onEvent);
    return () => currentRef?.removeEventListener('webkitendfullscreen', onEvent);
  });

  useEffect(() => {
    if (startTime && videoRef?.current?.currentTime !== undefined) {
      videoRef.current.currentTime = startTime;
    }
  }, [videoRef, startTime]);

  return (
    <OverlayMedia onClose={() => closeVideoHandler()} aspectRatio={aspectRatio}>
      <FullVideo crossOrigin="anonymous" ref={videoRef} src={src} controls />
    </OverlayMedia>
  );
}
